import { useState, useEffect, useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { unwrapSubmittedBidderList } from '../utils/tenderEvaluationHelpers';

const useSubmittedBidder = (
  procurementId,
  {
    pageNumber = 1,
    pageSize = 20,
    enabled = true,
    isActive = true,
    refreshToken = 0,
  } = {}
) => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(
    Boolean(enabled && procurementId && isActive)
  );

  const fetchSubmittedBidders = useCallback(
    async ({ silent = false } = {}) => {
      if (!procurementId || !enabled) return null;

      const token = getAuthToken();
      if (!token) {
        notify.error(MESSAGES.authRequired);
        return null;
      }

      if (!silent) setIsLoading(true);

      try {
        const url = `${api.API_URL}/dmut/get-submitted-bidder?procurementId=${encodeURIComponent(procurementId)}&PageNumber=${pageNumber}&PageSize=${pageSize}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const data = unwrapSubmittedBidderList(result);
        setItems(data.items);
        setTotalItems(data.totalItems);
        return data.items;
      } catch (err) {
        notify.apiError(err, 'load submitted bidder data');
        setItems([]);
        setTotalItems(0);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [procurementId, pageNumber, pageSize, enabled]
  );

  const refresh = useCallback(
    () => fetchSubmittedBidders(),
    [fetchSubmittedBidders]
  );

  useEffect(() => {
    if (!enabled || !procurementId || !isActive) return;
    fetchSubmittedBidders();
  }, [enabled, procurementId, isActive, fetchSubmittedBidders, refreshToken]);

  return {
    items,
    totalItems,
    isLoading,
    refresh,
  };
};

export default useSubmittedBidder;
